<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate ? null : partner.title" :back="listPath">
            <v-chip v-if="!isCreate && !loadingEntity" small :color="partner.approved ? 'success' : 'error'" :outlined="!partner.approved">
                {{ partner.approved ? 'Approved' : 'Pending approval' }}
            </v-chip>
            <v-btn v-if="isShow && !isDealer" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-title class="section-title">Business</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.title" :rules="required" label="Title *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="partner.category"
                                :items="categories"
                                item-text="title"
                                item-value="id"
                                label="Category"
                                :clearable="!readOnly"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.type" label="Type" outlined />
                        </v-col>
                        <v-col v-if="!isDealer" cols="12" md="6">
                            <v-autocomplete
                                v-model="partner.dealer"
                                :items="dealers"
                                :item-text="fullName"
                                item-value="id"
                                label="Dealer"
                                prepend-inner-icon="mdi-handshake-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.number="partner.promoSorting"
                                type="number"
                                label="Promoted position"
                                hint="Promoted partners are listed first, in this order. Leave empty for normal ordering."
                                :clearable="!readOnly"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex flex-wrap checkbox-row">
                                <v-checkbox v-model="partner.available" label="Available" hide-details />
                                <v-checkbox v-model="partner.b2b" label="B2B" hide-details />
                                <v-checkbox v-if="!isDealer" v-model="partner.approved" label="Approved" hide-details />
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Contact & location</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.firstName" label="Contact first name" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.lastName" label="Contact last name" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.phone" label="Phone" prepend-inner-icon="mdi-phone-outline" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.email" label="Email" prepend-inner-icon="mdi-email-outline" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="partner.address" label="Address" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.trim="partner.coordinates"
                                :rules="coordinateRules"
                                label="Coordinates"
                                placeholder="37.9838, 23.7275"
                                outlined
                            >
                                <template v-if="partner.coordinates" v-slot:append>
                                    <v-btn icon small :href="mapsUrl" target="_blank" title="Open in Google Maps">
                                        <v-icon small>mdi-map-marker-outline</v-icon>
                                    </v-btn>
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-tabs v-model="languageTab" class="px-2">
                    <v-tab>Ελληνικά</v-tab>
                    <v-tab>English</v-tab>
                </v-tabs>
                <v-divider />
                <v-card-text>
                    <v-tabs-items v-model="languageTab">
                        <v-tab-item eager>
                            <span class="editor-label">Description</span>
                            <editor v-model="partner.description" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                        <v-tab-item eager>
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model.trim="partner.titleEn" label="Title in English" outlined />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model.trim="partner.typeEn" label="Type in English" outlined />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field v-model.trim="partner.addressEn" label="Address in English" outlined />
                                </v-col>
                            </v-row>
                            <span class="editor-label">Description in English</span>
                            <editor v-model="partner.descriptionEn" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
            </v-card>

            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="fill-height">
                        <v-card-title class="section-title">
                            VAT numbers
                            <v-spacer />
                            <v-btn v-if="!readOnly" small text color="primary" @click="vats.push({ number: '' })">
                                <v-icon left small>mdi-plus</v-icon>Add
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-if="!vats.length" class="text--secondary">No VAT numbers.</div>
                            <div v-for="(vat, index) in vats" :key="vat.id || `new-${index}`" class="d-flex align-center">
                                <v-text-field
                                    v-model.trim="vat.number"
                                    :rules="required"
                                    label="VAT number (ΑΦΜ)"
                                    outlined
                                    dense
                                />
                                <v-btn v-if="!readOnly" icon class="ml-2 mb-6" title="Remove" @click="removeVat(index)">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card outlined class="fill-height">
                        <v-card-title class="section-title">
                            Reward rules
                            <v-spacer />
                            <v-btn v-if="!readOnly" small text color="primary" @click="addRewardRule">
                                <v-icon left small>mdi-plus</v-icon>Add
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-if="!rewardRules.length" class="text--secondary">No reward rules.</div>
                            <div v-for="(rule, index) in rewardRules" :key="rule.id || `new-${index}`" class="reward-rule">
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-select
                                            v-model="rule.type"
                                            :items="ruleTypes"
                                            :rules="required"
                                            label="Type"
                                            outlined
                                            dense
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model.number="rule.amount"
                                            :rules="required"
                                            :suffix="rule.type === 'percentage' ? '%' : '€'"
                                            type="number"
                                            label="Amount"
                                            outlined
                                            dense
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <date-field v-model="rule.dateFrom" label="From" :readonly="readOnly" dense />
                                    </v-col>
                                    <v-col cols="6">
                                        <date-field v-model="rule.dateTo" label="To" :readonly="readOnly" dense />
                                    </v-col>
                                </v-row>
                                <div v-if="!readOnly" class="text-right">
                                    <v-btn x-small text color="error" @click="removeRewardRule(index)">Remove rule</v-btn>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-card outlined class="mt-4 mb-4">
                <v-card-title class="section-title">Image</v-card-title>
                <v-card-text>
                    <image-input v-model="image" :readonly="readOnly" />
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create partner' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import vatService from '@/services/vat-service'
    import rewardRuleService from '@/services/reward-rule-service'
    import partnerService from '@/services/partner-service'
    import dealerService from '@/services/dealer-service'
    import imageService from '@/services/image-service'
    import Editor from '@tinymce/tinymce-vue'
    import formPage from '@/mixins/form-page'
    import imageUpload from '@/mixins/image-upload'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import ImageInput from '@/components/ImageInput'
    import { editorConfig, tinymceApiKey } from '@/utils/editor'
    import { entityId, fullName, pick, toInputDate } from '@/utils/format'

    const PARTNER_FIELDS = [
        'title', 'description', 'available', 'address', 'coordinates', 'titleEn', 'descriptionEn', 'addressEn',
        'type', 'typeEn', 'dealer', 'approved', 'afm', 'promoSorting', 'category', 'b2b', 'phone', 'email',
        'firstName', 'lastName'
    ]

    function emptyPartner() {
        return {
            title: '',
            description: '',
            available: true,
            b2b: false,
            approved: true,
            address: '',
            coordinates: '',
            titleEn: '',
            descriptionEn: '',
            addressEn: '',
            type: '',
            typeEn: '',
            afm: null,
            promoSorting: null,
            phone: '',
            email: '',
            firstName: '',
            lastName: '',
            category: null,
            dealer: null
        }
    }

    export default {
        name: 'PartnerForm',
        components: { Editor, PageHeader, DateField, ImageInput },
        mixins: [formPage, imageUpload],
        data: () => {
            return {
                entityLabel: 'Partner',
                tinymceApiKey,
                editorInit: editorConfig({ height: 320 }),
                partnerId: null,
                partner: emptyPartner(),
                vats: [],
                originalVats: [],
                rewardRules: [],
                removedRuleIds: [],
                categories: [],
                dealers: [],
                languageTab: 0,
                ruleTypes: [
                    { text: 'Percentage', value: 'percentage' },
                    { text: 'Fixed amount', value: 'fixed' }
                ],
                coordinateRules: [
                    v => !v || /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(v) || 'Use "latitude, longitude"'
                ]
            }
        },
        computed: {
            isDealer() {
                return this.$store.getters.isDealer
            },
            mapsUrl() {
                return `https://www.google.com/maps?q=${encodeURIComponent(this.partner.coordinates || '')}`
            }
        },
        created() {
            partnerService.getAllCategories().then(resp => {
                this.categories = resp.data.data
            })
            if (!this.isDealer) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                })
            }
            if (this.isCreate) {
                return
            }
            this.partnerId = parseInt(this.$route.params.partnerId)
            this.loadingEntity = true
            partnerService.getPartnerById(this.partnerId).then(resp => {
                const partner = resp.data.partner
                this.partner = { ...emptyPartner(), ...partner, category: entityId(partner.category) }
                this.vats = (partner.vatNumbers || []).map(vat => ({ id: vat.id, number: vat.number }))
                this.originalVats = this.vats.map(vat => ({ ...vat }))
                this.rewardRules = (partner.rewardRules || []).map(rule => ({
                    id: rule.id,
                    type: rule.type,
                    amount: rule.amount,
                    dateFrom: toInputDate(rule.dateFrom),
                    dateTo: toInputDate(rule.dateTo)
                }))
                this.setExistingImage(partner.image)
            }).catch(error => {
                this.$notify.error(error, 'Could not load the partner')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            fullName,
            addRewardRule() {
                this.rewardRules.push({ type: 'percentage', amount: null, dateFrom: null, dateTo: null })
            },
            removeRewardRule(index) {
                const [rule] = this.rewardRules.splice(index, 1)
                if (rule.id) {
                    this.removedRuleIds.push(rule.id)
                }
            },
            removeVat(index) {
                this.vats.splice(index, 1)
            },
            buildPayload() {
                const payload = pick(this.partner, PARTNER_FIELDS)
                payload.dealer = entityId(this.partner.dealer)
                payload.category = entityId(this.partner.category)
                payload.promoSorting = this.partner.promoSorting === '' ? null : this.partner.promoSorting
                payload.rewardRules = this.rewardRules.map(rule => {
                    const normalized = pick(rule, ['type', 'amount', 'dateFrom', 'dateTo'])
                    if (rule.id) {
                        normalized.id = rule.id
                    }
                    return normalized
                })
                return payload
            },
            // VAT numbers have their own endpoints, so they are synced after the partner itself is saved
            syncVats(partnerId) {
                const requests = []
                const keptIds = this.vats.filter(vat => vat.id).map(vat => vat.id)
                this.originalVats.filter(vat => !keptIds.includes(vat.id)).forEach(vat => {
                    requests.push(vatService.deleteVatById(vat.id))
                })
                this.vats.forEach(vat => {
                    if (!vat.id) {
                        requests.push(vatService.createVat({ number: vat.number, partner: partnerId }))
                        return
                    }
                    const original = this.originalVats.find(item => item.id === vat.id)
                    if (original && original.number !== vat.number) {
                        requests.push(vatService.editVatById(vat.id, { number: vat.number, partner: partnerId }))
                    }
                })
                return Promise.all(requests)
            },
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                let savedId = null
                const request = this.isEdit
                    ? partnerService.editPartnerById(this.partnerId, this.buildPayload())
                    : partnerService.createPartner(this.buildPayload())
                request.then(resp => {
                    savedId = this.isEdit ? this.partnerId : resp.data.id
                    return Promise.all([
                        this.syncVats(savedId),
                        this.saveImage('partnerId', savedId, imageService.uploadNewImage),
                        ...this.removedRuleIds.map(ruleId => rewardRuleService.deleteRewardRuleById(ruleId))
                    ])
                }).then(() => {
                    this.$notify.success(this.isCreate ? 'Partner created' : 'Partner saved')
                    this.goBack()
                }).catch(error => {
                    if (!savedId) {
                        this.$notify.error(error, 'Could not save the partner')
                        return
                    }
                    this.$notify.error('The partner was saved, but some VAT numbers, reward rules or the image could not be saved.')
                    if (this.isCreate) {
                        this.goToEntity(savedId, 'edit')
                    }
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>

<style scoped>

.reward-rule {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    padding: 12px 12px 4px;
    margin-bottom: 12px;
}

</style>
